export const flags = {
  en: "🇬🇧",
  ro: "🇷🇴",
  es: "🇪🇸",
  fr: "🇫🇷",
  it: "🇮🇹",
  de: "🇩🇪"
};

export const languageNames = {
  en: "English",
  ro: "Română",
  es: "Español",
  fr: "Français",
  it: "Italiano",
  de: "Deutsch"
};

export const translations = {
  en: {
    nav: { app: "Go to App" },
    hero: {
      badge: "Optimized for Suno V5.5",
      title1: "The Ultimate",
      titleHighlight: "Prompt Architect",
      title2: "for AI Music",
      subtitle: "Analyze YouTube references and engineer perfectly structured hybrid-format prompts. Master AI music generation with advanced meta-tags and structure control.",
      cta: "Launch Architect"
    },
    features: {
      title: "Empowering Creators Worldwide",
      subtitle: "From Los Angeles to Tokyo, producers use SunoSync to bridge the gap between creative ideas and perfect AI execution.",
      items: [
        { title: "YouTube Analysis", desc: "Extract BPM, genre, and mood from any reference track to guide your Suno generation." },
        { title: "Hybrid Formatting", desc: "Automatically format prompts using Suno's recommended hybrid prose and bracket system." },
        { title: "Global Meta-Tags", desc: "Access our comprehensive dictionary of production, vocal, and dynamic tags to control the AI." }
      ]
    },
    faq: {
      title: "Answer Engine Optimization (AEO)",
      subtitle: "Direct answers to the most common questions about AI music generation.",
      items: [
        { q: "What is the best way to prompt Suno V5.5?", a: "The most effective method for Suno V5.5 is the <strong>Hybrid Format</strong>. This involves combining fluid, descriptive prose with strict instructional meta-tags inside brackets (e.g., <code>[Chorus | crescendo]</code>). SunoSync automates this exact formatting." },
        { q: "Can I recreate a YouTube song in Suno AI?", a: "While you cannot paste a YouTube URL directly into Suno to copy a song, SunoSync acts as a bridge. By analyzing the reference track's metadata, genre, and structure, SunoSync builds a prompt that recreates the <em>vibe and style</em> of the original song without infringing on copyright." },
        { q: "How do I control the song structure in Suno?", a: "Song structure is controlled through precise lyric formatting and meta-tags. Using tags like <code>[Verse]</code>, <code>[Pre-Chorus]</code>, and <code>[Drop]</code> combined with specific syllable counts dictates when the AI transitions. SunoSync provides a full tag dictionary to help you master this." }
      ]
    },
    footer: "SunoSync Pro. Designed for global music innovators."
  },
  ro: {
    nav: { app: "Către Aplicație" },
    hero: {
      badge: "Optimizat pentru Suno V5.5",
      title1: "Cel Mai Bun",
      titleHighlight: "Arhitect de Prompturi",
      title2: "pentru Muzică AI",
      subtitle: "Analizează referințe YouTube și construiește prompturi perfect structurate în format hibrid. Stăpânește generarea de muzică AI cu meta-etichete avansate.",
      cta: "Lansează Arhitectul"
    },
    features: {
      title: "Susținem Creatorii Globali",
      subtitle: "De la Los Angeles la Tokyo, producătorii folosesc SunoSync pentru a reduce distanța dintre ideile creative și execuția perfectă a AI-ului.",
      items: [
        { title: "Analiză YouTube", desc: "Extrage BPM-ul, genul și starea de spirit din orice piesă de referință pentru a ghida generarea în Suno." },
        { title: "Formatare Hibridă", desc: "Formatează automat prompturile folosind sistemul hibrid recomandat de Suno, cu proză și paranteze." },
        { title: "Meta-Etichete Globale", desc: "Accesează dicționarul nostru complet de etichete de producție, vocale și dinamice pentru a controla AI-ul." }
      ]
    },
    faq: {
      title: "Optimizare pentru Motoarele de Răspuns (AEO)",
      subtitle: "Răspunsuri directe la cele mai comune întrebări despre generarea de muzică AI.",
      items: [
        { q: "Care este cel mai bun mod de a formula un prompt în Suno V5.5?", a: "Cea mai eficientă metodă pentru Suno V5.5 este <strong>Formatul Hibrid</strong>. Acesta implică combinarea prozei fluide cu meta-etichete stricte în paranteze (ex. <code>[Chorus | crescendo]</code>). SunoSync automatizează această formatare." },
        { q: "Pot recrea o melodie de pe YouTube în Suno AI?", a: "Deși nu poți introduce direct un URL de YouTube în Suno, SunoSync acționează ca o punte. Analizând metadatele, genul și structura, SunoSync construiește un prompt care recreează <em>vibrația și stilul</em> piesei originale." },
        { q: "Cum controlez structura melodiei în Suno?", a: "Structura melodiei este controlată prin formatarea precisă a versurilor și meta-etichete. Folosind etichete precum <code>[Verse]</code>, <code>[Pre-Chorus]</code> și numărarea silabelor dictezi tranzițiile AI-ului." }
      ]
    },
    footer: "SunoSync Pro. Conceput pentru inovatorii muzicii globale."
  },
  es: {
    nav: { app: "Ir a la App" },
    hero: {
      badge: "Optimizado para Suno V5.5",
      title1: "El Definitivo",
      titleHighlight: "Arquitecto de Prompts",
      title2: "para Música IA",
      subtitle: "Analiza referencias de YouTube y diseña prompts perfectamente estructurados en formato híbrido. Domina la generación de música con IA mediante metaetiquetas avanzadas.",
      cta: "Lanzar Arquitecto"
    },
    features: {
      title: "Empoderando Creadores Globales",
      subtitle: "Desde Los Ángeles hasta Tokio, los productores usan SunoSync para acortar la brecha entre ideas creativas y la ejecución perfecta de la IA.",
      items: [
        { title: "Análisis de YouTube", desc: "Extrae BPM, género y estado de ánimo de cualquier pista de referencia para guiar tu generación en Suno." },
        { title: "Formato Híbrido", desc: "Formatea automáticamente los prompts utilizando el sistema híbrido recomendado por Suno." },
        { title: "Metaetiquetas Globales", desc: "Accede a nuestro completo diccionario de etiquetas de producción, vocales y dinámicas." }
      ]
    },
    faq: {
      title: "Optimización para Motores de Respuesta (AEO)",
      subtitle: "Respuestas directas a las preguntas más comunes sobre la generación de música con IA.",
      items: [
        { q: "¿Cuál es la mejor manera de crear un prompt en Suno V5.5?", a: "El método más efectivo es el <strong>Formato Híbrido</strong>. Consiste en combinar prosa fluida con metaetiquetas instruccionales estrictas entre corchetes (ej. <code>[Chorus | crescendo]</code>)." },
        { q: "¿Puedo recrear una canción de YouTube en Suno AI?", a: "Aunque no puedes pegar una URL directamente, SunoSync actúa como un puente. Al analizar los metadatos, SunoSync crea un prompt que recrea la <em>vibra y estilo</em> de la canción original." },
        { q: "¿Cómo controlo la estructura de la canción?", a: "La estructura se controla mediante el formato preciso de las letras y metaetiquetas. Usando etiquetas como <code>[Verse]</code> y <code>[Drop]</code> dictas cuándo hace las transiciones la IA." }
      ]
    },
    footer: "SunoSync Pro. Diseñado para innovadores musicales globales."
  },
  fr: {
    nav: { app: "Aller à l'App" },
    hero: {
      badge: "Optimisé pour Suno V5.5",
      title1: "L'Ultime",
      titleHighlight: "Architecte de Prompts",
      title2: "pour Musique IA",
      subtitle: "Analysez les références YouTube et concevez des prompts hybrides parfaitement structurés. Maîtrisez la génération de musique IA avec des balises méta avancées.",
      cta: "Lancer l'Architecte"
    },
    features: {
      title: "Donner le Pouvoir aux Créateurs",
      subtitle: "De Los Angeles à Tokyo, les producteurs utilisent SunoSync pour combler l'écart entre l'idée créative et l'exécution parfaite de l'IA.",
      items: [
        { title: "Analyse YouTube", desc: "Extrayez le BPM, le genre et l'ambiance de n'importe quel morceau de référence pour guider Suno." },
        { title: "Formatage Hybride", desc: "Formatez automatiquement les prompts en utilisant le système hybride recommandé par Suno." },
        { title: "Balises Méta Globales", desc: "Accédez à notre dictionnaire complet de balises de production, vocales et dynamiques." }
      ]
    },
    faq: {
      title: "Optimisation pour Moteurs de Réponse (AEO)",
      subtitle: "Réponses directes aux questions les plus fréquentes sur la génération de musique IA.",
      items: [
        { q: "Quelle est la meilleure façon de formuler un prompt sur Suno V5.5 ?", a: "La méthode la plus efficace est le <strong>Format Hybride</strong>. Cela implique de combiner une prose fluide avec des balises d'instruction strictes (ex. <code>[Chorus | crescendo]</code>)." },
        { q: "Puis-je recréer une chanson YouTube dans Suno AI ?", a: "Bien que vous ne puissiez pas coller directement une URL, SunoSync agit comme un pont en analysant les métadonnées pour recréer <em>l'ambiance et le style</em> de la chanson." },
        { q: "Comment contrôler la structure de la chanson ?", a: "La structure est contrôlée via le formatage précis des paroles et des balises méta comme <code>[Verse]</code> ou <code>[Drop]</code>." }
      ]
    },
    footer: "SunoSync Pro. Conçu pour les innovateurs musicaux mondiaux."
  },
  it: {
    nav: { app: "Vai all'App" },
    hero: {
      badge: "Ottimizzato per Suno V5.5",
      title1: "Il Definitivo",
      titleHighlight: "Architetto di Prompt",
      title2: "per la Musica IA",
      subtitle: "Analizza le reference di YouTube e progetta prompt in formato ibrido perfettamente strutturati. Padroneggia la generazione musicale IA con meta-tag avanzati.",
      cta: "Lancia Architetto"
    },
    features: {
      title: "Potenziare i Creatori a Livello Globale",
      subtitle: "Da Los Angeles a Tokyo, i produttori usano SunoSync per colmare il divario tra l'idea creativa e l'esecuzione perfetta dell'IA.",
      items: [
        { title: "Analisi YouTube", desc: "Estrai BPM, genere e umore da qualsiasi traccia di riferimento per guidare la tua generazione su Suno." },
        { title: "Formattazione Ibrida", desc: "Formatta automaticamente i prompt usando il sistema ibrido raccomandato da Suno." },
        { title: "Meta-Tag Globali", desc: "Accedi al nostro dizionario completo di tag di produzione, vocali e dinamici." }
      ]
    },
    faq: {
      title: "Ottimizzazione per Motori di Risposta (AEO)",
      subtitle: "Risposte dirette alle domande più comuni sulla generazione di musica IA.",
      items: [
        { q: "Qual è il modo migliore per creare un prompt in Suno V5.5?", a: "Il metodo più efficace è il <strong>Formato Ibrido</strong>. Questo comporta la combinazione di prosa fluida con rigidi meta-tag istruttivi tra parentesi (es. <code>[Chorus | crescendo]</code>)." },
        { q: "Posso ricreare una canzone di YouTube in Suno AI?", a: "SunoSync funge da ponte. Analizzando i metadati, costruisce un prompt che ricrea <em>l'atmosfera e lo stile</em> del brano originale senza infrangere il copyright." },
        { q: "Come controllo la struttura della canzone?", a: "La struttura è controllata attraverso la precisa formattazione dei testi e i meta-tag come <code>[Verse]</code>, <code>[Pre-Chorus]</code> e <code>[Drop]</code>." }
      ]
    },
    footer: "SunoSync Pro. Progettato per innovatori musicali globali."
  },
  de: {
    nav: { app: "Zur App" },
    hero: {
      badge: "Optimiert für Suno V5.5",
      title1: "Der Ultimative",
      titleHighlight: "Prompt-Architekt",
      title2: "für KI-Musik",
      subtitle: "Analysiere YouTube-Referenzen und entwerfe perfekt strukturierte hybride Prompts. Meistere die KI-Musikgenerierung mit fortschrittlichen Meta-Tags.",
      cta: "Architekt Starten"
    },
    features: {
      title: "Weltweite Stärkung von Entwicklern",
      subtitle: "Von Los Angeles bis Tokio nutzen Produzenten SunoSync, um die Lücke zwischen kreativen Ideen und perfekter KI-Ausführung zu schließen.",
      items: [
        { title: "YouTube-Analyse", desc: "Extrahiere BPM, Genre und Stimmung aus jedem Referenztrack, um deine Suno-Generierung zu steuern." },
        { title: "Hybride Formatierung", desc: "Formatiere Prompts automatisch mit dem von Suno empfohlenen hybriden System." },
        { title: "Globale Meta-Tags", desc: "Greife auf unser umfassendes Wörterbuch für Produktions-, Gesangs- und Dynamik-Tags zu." }
      ]
    },
    faq: {
      title: "Antwortmaschinen-Optimierung (AEO)",
      subtitle: "Direkte Antworten auf die häufigsten Fragen zur KI-Musikgenerierung.",
      items: [
        { q: "Was ist der beste Weg, um Suno V5.5 zu prompten?", a: "Die effektivste Methode ist das <strong>Hybride Format</strong>. Hierbei wird fließende Prosa mit strikten Meta-Tags in Klammern kombiniert (z. B. <code>[Chorus | crescendo]</code>)." },
        { q: "Kann ich ein YouTube-Lied in Suno AI nachbilden?", a: "SunoSync fungiert als Brücke. Durch die Analyse der Metadaten erstellt SunoSync einen Prompt, der die <em>Stimmung und den Stil</em> des Originalsongs nachbildet." },
        { q: "Wie steuere ich die Songstruktur?", a: "Die Struktur wird durch präzise Textformatierung und Meta-Tags wie <code>[Verse]</code> oder <code>[Drop]</code> gesteuert." }
      ]
    },
    footer: "SunoSync Pro. Entwickelt für globale Musikinnovatoren."
  }
};